import { useCallback, useState } from "react";

import { isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { FEED_JOBS_STATUSES, IS_DEV } from "globals/constants";
import { useDictionary } from "hooks";

import { getStoredFeedJobId, notifyError, storeFeedJobId } from '../utils';
import {
	getJobStatus,
	isJobFinishedAndOutdated,
	exportCsv,
	downloadCsvFile,
	getCsvBlob
} from "../utils/csv";

export const useDownloadCsv = () => {
	const [ isDownloading, setIsDownloading ] = useState(false);

	const dictionary = useDictionary("common.downloadCsv");

	// useEffect(() => {
	// TODO: uncomment when decide what to do when user reopens a closed page
	// const jobId = getStoredFeedJobId();
	//
	// if (isNonEmptyString(jobId)) {
	// 	continueDownload(jobId);
	// }
	// }, []);

	const continueDownload = useCallback(async jobId => {
		try {
			const finishedJob = await getJobStatus(jobId);

			if (!isJobFinishedAndOutdated(finishedJob.status)) {
				downloadCsvFile(finishedJob);
			}
		} catch (e) {
			const message = IS_DEV ? e.message : dictionary.error
				|| "Unable to generate report. Please try again. If the issue persists, please contact support.";

			notifyError(message, e);
		} finally {
			// remove job from storage if link is invalid (more than 24 h)
			storeFeedJobId("");
		}
	}, [ dictionary.error ]);

	const download = useCallback(async (payload, loadReport = false) => {
		setIsDownloading(true);

		try {
			const response = await exportCsv(payload);

			if (isNonEmptyObject(response.error) || response.status === FEED_JOBS_STATUSES.FAILED) {
				const message = IS_DEV
					? response.message
					: dictionary.error ||
						"Unable to generate report. Please try again. If the issue persists, please contact support.";

				notifyError(message, response);
			} else {
				if (loadReport) {
					const csvBlobResponse = await getCsvBlob(
						response.job.resultUrl
					);

					if (
						csvBlobResponse.error &&
						csvBlobResponse.status === "ERROR"
					) {
						const message =
							dictionary.errorLoadingBlob ??
							"Unable to load Report data.";

						notifyError(message, csvBlobResponse.error);
					} else {
						return csvBlobResponse;
					}

					if (
						isNonEmptyObject(csvBlobResponse.error) &&
						csvBlobResponse.status === "ERROR"
					) {
						const message = IS_DEV
							? csvBlobResponse.message
							: dictionary.errorLoadingBlob ||
								"Unable to load Report data.";

						notifyError(message, csvBlobResponse.error);
					}

					return csvBlobResponse;
				} else {
					downloadCsvFile(response.job);
				}
			}
		} catch (error) {
			const message = IS_DEV
				? (error?.message ?? 'Network error')
				: dictionary.error ||
				'Unable to generate report. Please try again. If the issue persists, please contact support.';
			notifyError(message, error);
			// todo: check clear feed job id in localStorage
		} finally {
			// remove job from storage if link is invalid (more than 24 h)
			storeFeedJobId("");
			setIsDownloading(false);
		}
	}, [ dictionary.error ]);

	return {
		isDownloading,
		download,
	};
};
